import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import '../../../styles/settingsNav.css';
import '../../../styles/teamsIntegration.css';
import { supabaseClient } from '../../../config/supabase';
import HoverHelp from '../../shared/HoverHelp';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import Switch from '../../ui/Switch';
import { useSaveToast } from '../../ui/SaveToast';
import { INTEGRATION_KEYS, canManageIntegration, useIntegrationAccess } from '../../../lib/integrations/integrationGate';
import {
    DIRECTORY_OPTIONS, DISCONNECT_OPTIONS, DEFAULT_INTEGRATION, DEFAULT_TEAMS_SETTINGS, OUTSIDE_OPTIONS, REPLY_OPTIONS, USE_SWITCHES, WHO_OPTIONS,
    assignTeamOwner, describeAuditRow, isSharePointUrl, loadAuditLog, loadTeamsAdmin, normalizeDomain, saveTeamsAdmin, settingsProblem,
} from '../../../lib/integrations/teamsSettings';
import { PillGroup, SwitchRow } from './TeamsControls';

export const TEAMS_ADMIN_PATH = '/admin/integrations/microsoft-teams';

const HELP = {
    title: 'Microsoft Teams integration',
    body: 'Decide whether your staff can mirror Microsoft Teams into IntelAgent, who gets it, and what it may use. Everything starts off. Microsoft 365 settings always win; these can only narrow them. Every save is written to the audit log.',
};

const STATE_WORDS = { opted_in: 'Not turned on yet', on: 'On', off: 'Turned off' };

function OwnerPopup({ team, people, busy, error, onSave, onClose }) {
    const [choice, setChoice] = useState(team.owner_user_id || '');
    return (
        <Modal3D
            portal
            title={team.owner_user_id ? 'Change Team Owner' : 'Assign Team Owner'}
            subtitle={`${team.name}: the owner runs this team's admins, members and settings inside the app.`}
            onClose={onClose}
            testId="teams-owner-popup"
            actions={(
                <>
                    <Button3D onClick={onClose} disabled={busy}>Cancel</Button3D>
                    {team.owner_user_id && <Button3D onClick={() => onSave(null)} disabled={busy}>Remove owner</Button3D>}
                    <Button3D variant="go" onClick={() => onSave(choice)} disabled={busy || !choice}>{busy ? 'Saving…' : 'Save'}</Button3D>
                </>
            )}
        >
            <div className="ia-teams__field">
                <label htmlFor="teams-owner-select">Team Owner</label>
                <select id="teams-owner-select" value={choice} onChange={(event) => setChoice(event.target.value)}>
                    <option value="">Choose a person</option>
                    {people.map((person) => <option key={person.user_id} value={person.user_id}>{person.full_name}</option>)}
                </select>
            </div>
            <p className="ia-teams__hint mt-2">The previous owner stays in the team as a Team Admin.</p>
            {error && <p role="alert" className="ia-teams__error">{error}</p>}
        </Modal3D>
    );
}

function AuditPopup({ client, people, onClose }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        loadAuditLog(client).then(setRows).catch((failure) => setError(failure?.message || 'The audit log could not be loaded.'));
    }, [client]);
    const nameOf = (id) => people.find((person) => person.user_id === id)?.full_name || 'System';
    return (
        <Modal3D portal size="wide" title="Teams audit log" subtitle="Every settings change, ownership change and connection, newest first." onClose={onClose}
            testId="teams-audit-popup" actions={<Button3D variant="go" onClick={onClose}>Done</Button3D>}>
            {error && <p role="alert" className="ia-teams__error">{error}</p>}
            {!rows && !error && <p>Loading…</p>}
            {rows && rows.length === 0 && <p>Nothing has been logged yet.</p>}
            {rows && rows.length > 0 && (
                <ul className="ia-teams__list" aria-label="Audit entries">
                    {rows.map((row) => (
                        <li key={row.id} className="ia-teams__audit-row">
                            <span className="ia-teams__hint">{new Date(row.created_at).toLocaleString()}</span>
                            <span>{nameOf(row.actor_id)} · {describeAuditRow(row)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </Modal3D>
    );
}

function ReviewPopup({ client, onClose }) {
    const [rows, setRows] = useState([]);
    const [health, setHealth] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        let active = true;
        (async () => {
            const gate = await client.rpc('integration_enabled_for_me', { p_key: INTEGRATION_KEYS.MICROSOFT_TEAMS });
            if (gate.error || gate.data !== true) throw new Error('Microsoft Teams is not available to you.');
            const [review, status] = await Promise.all([client.from('teams_review').select('id,resource,reason,created_at').order('created_at', { ascending: false }), client.rpc('teams_mirror_health')]);
            if (review.error || status.error) throw review.error || status.error;
            if (active) { setRows(review.data || []); setHealth(status.data); }
        })().catch(failure => { if (active) setError(failure.message || 'Teams review could not be loaded.'); });
        return () => { active = false; };
    }, [client]);
    return <Modal3D portal title="Teams review and mirror health" onClose={onClose} actions={<Button3D onClick={onClose}>Done</Button3D>}>
        {error && <p role="alert">{error}</p>}
        {health && <p role={health.stale ? 'alert' : undefined}>Last received: {health.last_received_at || 'No message yet'}.
            {health.stale && ' Mirror needs attention. Ask the person to reconnect.'} {health.last_error}</p>}
        {!rows.length && !error && <p>No outside conversations waiting for review.</p>}
        {rows.map(row => <p key={row.id}>{row.resource}: {row.reason}</p>)}
        <p>Correct the company policy or CRM contact, then refresh Teams. No message is copied while it is blocked.</p>
    </Modal3D>;
}

// Teams PR A (board 5): Admin Center > Integrations > Microsoft Teams. Tenant Admins only (the route admits only them, this page
// re-checks the database's own answer, and every table refuses anyone else). Its own page, no left panel, like Events Settings.
export default function TeamsAdminPage({ userId, orgId, theme = 'light', client = supabaseClient }) {
    const access = useIntegrationAccess(INTEGRATION_KEYS.MICROSOFT_TEAMS, userId, client);
    const toast = useSaveToast();
    const [data, setData] = useState(null);
    const [integration, setIntegration] = useState({ ...DEFAULT_INTEGRATION });
    const [settings, setSettings] = useState({ ...DEFAULT_TEAMS_SETTINGS });
    const [loadError, setLoadError] = useState('');
    const [problem, setProblem] = useState('');
    const [saving, setSaving] = useState(false);
    const [domainDraft, setDomainDraft] = useState('');
    const [siteDraft, setSiteDraft] = useState({ name: '', url: '' });
    const [ownerTeam, setOwnerTeam] = useState(null);
    const [ownerError, setOwnerError] = useState('');
    const [auditOpen, setAuditOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const allowed = canManageIntegration(access);

    const load = useCallback(async () => {
        try {
            const next = await loadTeamsAdmin(client, orgId);
            setData(next);
            setIntegration(next.integration);
            setSettings(next.settings);
            setLoadError('');
        } catch (failure) {
            setLoadError(failure?.message || 'The Teams settings could not be loaded.');
        }
    }, [client, orgId]);

    useEffect(() => { if (allowed && orgId) load(); }, [allowed, orgId, load]);

    if (access.loading) return <div className="p-8 text-center text-[var(--ink-muted)]">Loading…</div>;
    if (!allowed) return <Navigate to="/admin" replace />;

    const set = (key) => (value) => setSettings((current) => ({ ...current, [key]: value }));
    const setI = (key) => (value) => setIntegration((current) => ({ ...current, [key]: value }));
    const toggleIn = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

    const addDomain = () => {
        const domain = normalizeDomain(domainDraft);
        if (!domain) { setProblem('That does not look like a domain. Try company.com.'); return; }
        setSettings((current) => ({ ...current, approved_domains: current.approved_domains.includes(domain) ? current.approved_domains : [...current.approved_domains, domain] }));
        setDomainDraft(''); setProblem('');
    };
    const addSite = () => {
        if (!isSharePointUrl(siteDraft.url)) { setProblem('A SharePoint site address starts with https:// and ends in .sharepoint.com/…'); return; }
        const site = { name: siteDraft.name.trim() || siteDraft.url.trim(), url: siteDraft.url.trim() };
        setSettings((current) => ({ ...current, allowed_sharepoint_sites: [...current.allowed_sharepoint_sites.filter((item) => item.url !== site.url), site] }));
        setSiteDraft({ name: '', url: '' }); setProblem('');
    };

    const save = async () => {
        const message = settingsProblem(settings, integration);
        if (message) { setProblem(message); return; }
        setProblem(''); setSaving(true);
        try {
            const saved = await saveTeamsAdmin(client, orgId, integration, settings);
            setIntegration(saved.integration); setSettings(saved.settings);
            toast.saved();
            access.refresh();
            load();
        } catch (failure) {
            const text = `Your Teams settings could not be saved. ${failure?.message || ''}`.trim();
            setProblem(text); toast.failed(text);
        } finally { setSaving(false); }
    };

    const saveOwner = async (personId) => {
        setSaving(true); setOwnerError('');
        try {
            await assignTeamOwner(client, ownerTeam.team_id, personId);
            toast.saved(); setOwnerTeam(null); load();
        } catch (failure) {
            setOwnerError(failure?.message || 'The Team Owner could not be changed.');
        } finally { setSaving(false); }
    };

    const groups = data?.teams || [];
    const linked = Boolean(settings.linked_m365_tenant_id);

    return (
        <div data-theme={theme} className="ia-set ia-teams-admin" data-testid="teams-admin" data-m6-component="TeamsAdmin">
            <nav aria-label="Breadcrumb" className="ia-set__crumbs">
                <ol>
                    <li className="ia-set__crumb-item"><Link to="/admin" className="ia-btn3d ia-btn3d--pill ia-set__crumb">Admin Center</Link><span aria-hidden="true" className="ia-set__crumb-sep">›</span></li>
                    <li className="ia-set__crumb-item"><span className="ia-set__crumb-current">Integrations</span><span aria-hidden="true" className="ia-set__crumb-sep">›</span></li>
                    <li className="ia-set__crumb-item"><span aria-current="page" className="ia-set__crumb-current">Microsoft Teams</span></li>
                </ol>
            </nav>
            <div className="ia-teams">
                <div className="ia-teams__topline">
                    <span>Status: <strong data-testid="teams-admin-state">{STATE_WORDS[access.state] || 'Not turned on yet'}</strong></span>
                    <span className="ia-teams__topline-spacer" />
                    <span>Tenant Admins only · authenticator required</span>
                </div>
                {loadError && <p role="alert" className="ia-teams__error">{loadError}</p>}
                {!data && !loadError && <p>Loading…</p>}
                {data && (
                    <>
                        <section className="ia-teams__card" aria-labelledby="teams-admin-title">
                            <div className="ia-teams__head">
                                <h1 id="teams-admin-title">Microsoft Teams integration</h1>
                                <HoverHelp helpId="integrations.teams.admin" content={HELP} userId={userId} />
                                <span className="ia-teams__spacer" />
                                <Switch checked={integration.enabled} onChange={setI('enabled')} aria-label="Teams integration" onText="ON" offText="OFF" />
                            </div>
                            <div className="ia-teams__line">
                                <span className={`ia-teams__status-dot${linked ? ' ia-teams__status-dot--on' : ''}`} aria-hidden="true" />
                                <span className="ia-teams__line-label">
                                    Linked Microsoft 365 organization: <strong>{linked ? (settings.linked_m365_tenant_name || settings.linked_m365_tenant_id) : 'not linked'}</strong>
                                    <span className="ia-teams__hint block">Staff can only connect Teams accounts from this organization.</span>
                                </span>
                            </div>
                            <div className="ia-teams__inline">
                                <div className="ia-teams__field">
                                    <label htmlFor="teams-tenant-id">Microsoft 365 organization (tenant) ID</label>
                                    <input id="teams-tenant-id" value={settings.linked_m365_tenant_id} onChange={(event) => set('linked_m365_tenant_id')(event.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
                                </div>
                                <div className="ia-teams__field">
                                    <label htmlFor="teams-tenant-name">Name shown here</label>
                                    <input id="teams-tenant-name" value={settings.linked_m365_tenant_name} onChange={(event) => set('linked_m365_tenant_name')(event.target.value)} placeholder="therideatl.com" />
                                </div>
                            </div>
                        </section>

                        <div className="ia-teams__row2">
                            <section className="ia-teams__card" aria-label="What IntelAgent may use">
                                <h2>What IntelAgent may use</h2>
                                <span className="ia-teams__hint">Everything starts off. Microsoft&apos;s own Teams settings always win; these can only narrow them.</span>
                                {USE_SWITCHES.map((item) => (
                                    <SwitchRow key={item.key} label={item.label} checked={settings[item.key]} onChange={set(item.key)} />
                                ))}
                                <PillGroup label="Replies from IntelAgent" options={REPLY_OPTIONS} value={settings.reply_mode} onChange={set('reply_mode')} testId="teams-reply-mode" />
                            </section>

                            <section className="ia-teams__card" aria-label="Who gets Teams">
                                <h2>Who gets Teams</h2>
                                <PillGroup options={WHO_OPTIONS} value={integration.assignment_mode} onChange={setI('assignment_mode')} testId="teams-who" />
                                {integration.assignment_mode === 'all_staff' && <span className="ia-teams__hint">Everyone in your organization who can open the Command Center.</span>}
                                {integration.assignment_mode === 'roles' && data.roles.map((role) => (
                                    <SwitchRow key={role.key} label={role.name} checked={integration.assigned_roles.includes(role.key)}
                                        onChange={() => setI('assigned_roles')(toggleIn(integration.assigned_roles, role.key))} />
                                ))}
                                {integration.assignment_mode === 'people' && (data.people.length === 0
                                    ? <span className="ia-teams__hint">No people yet.</span>
                                    : data.people.map((person) => (
                                        <SwitchRow key={person.user_id} label={person.full_name} checked={integration.assigned_user_ids.includes(person.user_id)}
                                            onChange={() => setI('assigned_user_ids')(toggleIn(integration.assigned_user_ids, person.user_id))} />
                                    )))}

                                <h2>Team Owners</h2>
                                <span className="ia-teams__hint">Only Tenant Admins assign Team Owners. Team Owners then run their own team&apos;s admins, members and settings inside the app.</span>
                                {groups.length === 0 && <span className="ia-teams__hint" data-testid="teams-no-teams">No IntelAgent teams yet. Add a Team or Group in Brands &amp; Locations.</span>}
                                {groups.map((team) => (
                                    <div key={team.team_id} className="ia-teams__line" data-team-row={team.team_id}>
                                        <span className="ia-teams__line-label">{team.name}</span>
                                        <span className="ia-teams__hint">Owner: {team.owner_name || 'not set'}</span>
                                        <Button3D onClick={() => { setOwnerError(''); setOwnerTeam(team); }}>{team.owner_user_id ? 'Change' : 'Assign owner'}</Button3D>
                                    </div>
                                ))}

                                <h2>People directory for new chats</h2>
                                <PillGroup options={DIRECTORY_OPTIONS} value={settings.directory_scope} onChange={set('directory_scope')} testId="teams-directory" />
                                {settings.directory_scope === 'groups' && groups.map((team) => (
                                    <SwitchRow key={team.team_id} label={team.name} checked={settings.directory_group_ids.includes(team.team_id)}
                                        onChange={() => set('directory_group_ids')(toggleIn(settings.directory_group_ids, team.team_id))} />
                                ))}
                            </section>
                        </div>

                        <section className="ia-teams__card" aria-label="People outside the company">
                            <h2>People outside the company</h2>
                            <PillGroup options={OUTSIDE_OPTIONS} value={settings.outside_people} onChange={set('outside_people')} testId="teams-outside" />
                            {settings.outside_people === 'approved_domains' && (
                                <>
                                    <div className="ia-teams__pills" aria-label="Approved domains">
                                        <span className="ia-teams__pills-label">Approved domains</span>
                                        {settings.approved_domains.map((domain) => (
                                            <span key={domain} className="ia-teams__chip">@{domain}
                                                <button type="button" aria-label={`Remove ${domain}`} onClick={() => set('approved_domains')(settings.approved_domains.filter((item) => item !== domain))}>✕</button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="ia-teams__inline">
                                        <div className="ia-teams__field">
                                            <label htmlFor="teams-domain">Add a domain</label>
                                            <input id="teams-domain" value={domainDraft} onChange={(event) => setDomainDraft(event.target.value)} placeholder="client-company.com" />
                                        </div>
                                        <Button3D onClick={addDomain}>Add domain</Button3D>
                                    </div>
                                    <SwitchRow label="Person must already be a CRM contact" checked={settings.require_crm_contact} onChange={set('require_crm_contact')} />
                                </>
                            )}
                            <SwitchRow label="Allow attachments from outside people" checked={settings.allow_outside_attachments} onChange={set('allow_outside_attachments')} />
                            <div className="ia-teams__line">
                                <span className="ia-teams__line-label">Everything else goes to the review list (not mirrored)</span>
                                <Button3D disabled={!access.enabledForMe} onClick={() => setReviewOpen(true)}>Review list / mirror health</Button3D>
                            </div>
                            <span className="ia-teams__hint">Outside messages always carry an &quot;External&quot; label. The AI never answers Teams threads.</span>
                        </section>

                        <div className="ia-teams__row2">
                            <section className="ia-teams__card" aria-label="Allowed SharePoint sites">
                                <h2>Allowed SharePoint sites</h2>
                                {settings.allowed_sharepoint_sites.length === 0 && <span className="ia-teams__hint">No sites allowed.</span>}
                                {settings.allowed_sharepoint_sites.map((site) => (
                                    <div key={site.url} className="ia-teams__line">
                                        <span className="ia-teams__line-label">{site.name}<span className="ia-teams__hint block">{site.url}</span></span>
                                        <Button3D onClick={() => set('allowed_sharepoint_sites')(settings.allowed_sharepoint_sites.filter((item) => item.url !== site.url))}>Remove</Button3D>
                                    </div>
                                ))}
                                <div className="ia-teams__inline">
                                    <div className="ia-teams__field">
                                        <label htmlFor="teams-site-name">Site name</label>
                                        <input id="teams-site-name" value={siteDraft.name} onChange={(event) => setSiteDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Operations" />
                                    </div>
                                    <div className="ia-teams__field">
                                        <label htmlFor="teams-site-url">Site address</label>
                                        <input id="teams-site-url" value={siteDraft.url} onChange={(event) => setSiteDraft((current) => ({ ...current, url: event.target.value }))} placeholder="https://company.sharepoint.com/sites/ops" />
                                    </div>
                                    <Button3D onClick={addSite}>Add site</Button3D>
                                </div>
                                <span className="ia-teams__hint">IntelAgent cannot see any other site. It never creates &quot;anyone with the link&quot; links.</span>
                            </section>
                            <section className="ia-teams__card" aria-label="Audit and disconnect">
                                <h2>Audit and disconnect</h2>
                                <div className="ia-teams__line">
                                    <span className="ia-teams__line-label">Audit log: settings changes and every outside message ({data.auditCount})</span>
                                    <Button3D onClick={() => setAuditOpen(true)}>Open log</Button3D>
                                </div>
                                <PillGroup label="When someone disconnects" options={DISCONNECT_OPTIONS} value={settings.disconnect_rule} onChange={set('disconnect_rule')} testId="teams-disconnect" />
                            </section>
                        </div>

                        {problem && <p role="alert" className="ia-teams__error">{problem}</p>}
                        <div className="ia-teams__actions">
                            <Button3D variant="go" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button3D>
                        </div>
                    </>
                )}
            </div>
            {ownerTeam && <OwnerPopup team={ownerTeam} people={data?.people || []} busy={saving} error={ownerError} onSave={saveOwner} onClose={() => setOwnerTeam(null)} />}
            {auditOpen && <AuditPopup client={client} people={data?.people || []} onClose={() => setAuditOpen(false)} />}
            {reviewOpen && access.enabledForMe && <ReviewPopup client={client} onClose={() => setReviewOpen(false)} />}
        </div>
    );
}
